import { Button, cn } from '@immo/ui-kit';
import { QUARTER_HOUR_STEP_SECONDS } from '@/lib/quarter-hour';

export type OccurredAtFieldProps = {
  readonly id: string;
  // 'YYYY-MM-DDTHH:mm' when complete; '' when both segments are empty.
  // A lone date or time (no 'T' counterpart) is surfaced as-is so the caller
  // can flag the incomplete state — see `localDateTimeState`.
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onReset: () => void;
  readonly resetLabel: string;
  readonly timeLabel: string;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly 'aria-describedby'?: string | undefined;
};

const SEGMENT_CLASS = [
  'min-w-0 border-0 bg-transparent p-0 text-base font-sans text-text-primary',
  'focus:outline-none disabled:cursor-not-allowed',
  '[&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60',
  'hover:[&::-webkit-calendar-picker-indicator]:opacity-100',
].join(' ');

function splitValue(value: string): readonly [date: string, time: string] {
  const separatorIndex = value.indexOf('T');

  if (separatorIndex === -1) return [value, ''];

  return [value.slice(0, separatorIndex), value.slice(separatorIndex + 1)];
}

function joinValue(date: string, time: string): string {
  return date === '' && time === '' ? '' : `${date}T${time}`;
}

// A single visually-unified date+time control — two native segments inside one
// bordered field, so it reads as one picker rather than two separate inputs.
export function OccurredAtField({
  id,
  value,
  onChange,
  onReset,
  resetLabel,
  timeLabel,
  disabled = false,
  invalid = false,
  ...rest
}: OccurredAtFieldProps) {
  const [date, time] = splitValue(value);
  const describedBy = rest['aria-describedby'];
  const timeId = `${id}-time`;

  return (
    <div
      className={cn(
        // Wraps rather than squeezing: in a narrow column the date segment would
        // otherwise shrink to 0px and vanish (IMM-867 QA-22).
        'flex min-h-control-md w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-surface px-3 py-1.5',
        'transition-colors duration-micro ease-out',
        'focus-within:border-interactive focus-within:shadow-focus',
        disabled && 'cursor-not-allowed opacity-60',
        invalid ? 'border-danger-500 focus-within:border-danger-500 focus-within:shadow-focus-danger' : 'border-border',
      )}
    >
      <input
        id={id}
        type="date"
        value={date}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(event) => onChange(joinValue(event.target.value, time))}
        className={cn(SEGMENT_CLASS, 'min-w-fit flex-1')}
      />
      <span aria-hidden className="text-text-tertiary">
        ·
      </span>
      <input
        id={timeId}
        type="time"
        // Every caller of this control is an activity time picker — see IMM-867.1.
        step={QUARTER_HOUR_STEP_SECONDS}
        value={time}
        disabled={disabled}
        aria-label={timeLabel}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(event) => onChange(joinValue(date, event.target.value))}
        className={cn(SEGMENT_CLASS, 'shrink-0')}
      />
      <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={onReset}>
        {resetLabel}
      </Button>
    </div>
  );
}
